// ✅ TicketSummary.js (Part 1 of 3)
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  Timestamp,
  query,
  where,
} from 'firebase/firestore';
import { Bar } from 'react-chartjs-2';
import { Link, useParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const colors = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#22c55e','#a855f7','#f97316'];

const TicketSummary = () => {
  const { schoolId } = useParams();

  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [yearlyOverride, setYearlyOverride] = useState(0);
  const [advisoryOverrides, setAdvisoryOverrides] = useState({});
  const [advisoryLabels, setAdvisoryLabels] = useState({});
  const [monthlyTotals, setMonthlyTotals] = useState({});
  const [staffStats, setStaffStats] = useState({});
  const [valueTypeCounts, setValueTypeCounts] = useState({});
  const [advisoryYearTotals, setAdvisoryYearTotals] = useState({});
  const [totalTickets, setTotalTickets] = useState(0);
  const [showAdvisory, setShowAdvisory] = useState(true);
  const [showMonthChart, setShowMonthChart] = useState(false);
  const [showStaffChart, setShowStaffChart] = useState(false);
  const [showValueChart, setShowValueChart] = useState(false);
  const [studentBalances, setStudentBalances] = useState({});
  const [pbisValues, setPbisValues] = useState([]); // ✅ Holds defined PBIS values

  useEffect(() => {
    const fetchData = async () => {
      let usersSnap;
      const usersCol = collection(db, 'users');
      if (schoolId) {
        usersSnap = await getDocs(query(usersCol, where('schoolId', '==', schoolId)));
        if (usersSnap.empty) {
          usersSnap = await getDocs(usersCol);
        }
      } else {
        usersSnap = await getDocs(usersCol);
      }
      // ✅ Fetch PBIS values from /schools/{schoolId}/pbisValues
const pbisRef = schoolId ? collection(db, 'schools', schoolId, 'pbisValues') : null;
if (pbisRef) {
  try {
    const snap = await getDocs(pbisRef);
    const values = snap.docs.map(doc => doc.data().name);
    setPbisValues(values);
  } catch (err) {
    console.error('Failed to fetch PBIS values:', err);
  }
}
      const userData = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(userData);

      let ticketsSnap;
      const ticketsCol = schoolId
        ? collection(db, 'schools', schoolId, 'tickets')
        : collection(db, 'tickets');

      ticketsSnap = await getDocs(ticketsCol);
      const ticketData = ticketsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTickets(ticketData);

      const totalsRef = schoolId
        ? doc(db, "schools", schoolId, "totals", "yearly")
        : doc(db, "totals", "yearly");

      const labelsRef = schoolId
        ? doc(db, "schools", schoolId, "totals", "classLabels")
        : doc(db, "totals", "advisoryLabels");

      let totalsSnap = await getDoc(totalsRef);
      if (!totalsSnap.exists()) {
        await setDoc(totalsRef, { totalTicketsOverride: 0, advisoryOverrides: {} }, { merge: true });
        totalsSnap = await getDoc(totalsRef);
      }

      if (!totalsSnap.exists() && schoolId === "nlcs") {
        const legacy = await getDoc(doc(db, "totals", "yearly"));
        if (legacy.exists()) totalsSnap = legacy;
      }

      if (totalsSnap.exists()) {
        const data = totalsSnap.data();
        setYearlyOverride(data.totalTicketsOverride || 0);
        setAdvisoryOverrides(data.advisoryOverrides || {});
      }

      let labelsSnap = await getDoc(labelsRef);
      if (!labelsSnap.exists() && schoolId) {
        labelsSnap = await getDoc(
          doc(db, "schools", schoolId, "totals", "advisoryLabels")
        );
      }
      if (!labelsSnap.exists() && schoolId === "nlcs") {
        const legacyLabels = await getDoc(doc(db, "totals", "advisoryLabels"));
        if (legacyLabels.exists()) labelsSnap = legacyLabels;
      }
      if (labelsSnap.exists()) {
        setAdvisoryLabels(labelsSnap.data() || {});
      }
    };

    fetchData();
  }, [schoolId]);
  useEffect(() => {
    const groupMap = {};
    const balances = {};

    users.forEach(u => {
      if (u.role === 'student') {
        const klass = u.class ?? u.advisory ?? 'Unassigned';
        groupMap[u.id] = klass;
      }
    });

    const monthly = {};
    const staff = {};
    const valuesByType = {};
    const advisoryTotals = {};
    let total = 0;

    tickets.forEach(ticket => {
      const qty = ticket.quantity ?? 1;
      const date =
        ticket.timestamp?.toDate?.() || ticket.createdAt?.toDate?.();
      const monthName = date ? months[date.getMonth()] : 'Unknown';
      const giver = ticket.givenBy?.name || 'Unknown';
      const type = ticket.valueType;
      const studentId = ticket.studentId;
      const grp = studentId ? groupMap[studentId] : undefined;

      if (!monthly[monthName]) monthly[monthName] = 0;
      monthly[monthName] += qty;

      if (!staff[monthName]) staff[monthName] = {};
      if (!staff[monthName][giver]) staff[monthName][giver] = 0;
      staff[monthName][giver] += qty;

      if (type) {
        if (!valuesByType[type]) valuesByType[type] = 0;
        valuesByType[type] += qty;
      }

      if (grp) {
        if (!advisoryTotals[grp]) advisoryTotals[grp] = 0;
        if (qty > 0) advisoryTotals[grp] += qty;
      }

      if (studentId) {
        if (!balances[studentId]) balances[studentId] = 0;
        balances[studentId] += qty;
      }

      if (qty > 0) total += qty;
    });

    Object.entries(advisoryOverrides || {}).forEach(([grp, val]) => {
      if (!advisoryTotals[grp]) advisoryTotals[grp] = 0;
      advisoryTotals[grp] += val;
    });

    setMonthlyTotals(monthly);
    setStaffStats(staff);
    setValueTypeCounts(valuesByType);
    setAdvisoryYearTotals(advisoryTotals);
    setTotalTickets(total + (yearlyOverride || 0));
    setStudentBalances(balances);
  }, [tickets, users, yearlyOverride, advisoryOverrides]);

  const getGroupDisplayName = (grp) => advisoryLabels[grp] || grp;

  const handleManualAdjustment = async (type, value, group = '', studentId = '') => {
    const totalsRef = schoolId
      ? doc(db, 'schools', schoolId, 'totals', 'yearly')
      : doc(db, 'totals', 'yearly');
  
    // Only initialize if it doesn't exist
    let totalsSnap = await getDoc(totalsRef);
    if (!totalsSnap.exists()) {
      await setDoc(totalsRef, {
        totalTicketsOverride: 0,
        advisoryOverrides: {},
      });
      totalsSnap = await getDoc(totalsRef);
    }
  
    const data = totalsSnap.data() || {};
  
    if (type === 'schoolTotal') {
      const current = Number(data.totalTicketsOverride || 0);
      const updated = current + value;
      await setDoc(totalsRef, { totalTicketsOverride: updated }, { merge: true });
      setYearlyOverride(updated); // ✅ Update local state
    }
  
    if (type === 'advisory' && group) {
      const current = data.advisoryOverrides || {};
      const currentVal = Number(current[group] ?? 0);
      const updated = { ...current, [group]: currentVal + value };
      await setDoc(totalsRef, { advisoryOverrides: updated }, { merge: true });
      setAdvisoryOverrides(updated); // ✅ Update local state
    }
  
    if (type === 'student' && studentId) {
      const currentBalance = studentBalances[studentId] || 0;
    
      // 🚫 Prevent negative balances
      if (currentBalance + value < 0) {
        alert("❌ Cannot reduce below 0 tickets");
        return;
      }
    
      const ticketPath = schoolId
        ? collection(db, 'schools', schoolId, 'tickets')
        : collection(db, 'tickets');
    
      await addDoc(ticketPath, {
        studentId,
        quantity: value,
        timestamp: Timestamp.now(),
        givenBy: { name: 'Manual Entry' },
        valueType: 'Manual',
        ...(schoolId ? { schoolId } : {}),
      });
    
      // Refresh tickets
      const snap = await getDocs(ticketPath);
      setTickets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
  };

  const groups = [...new Set(
    users
      .filter(u => u.role === 'student')
      .map(u => (u.class ?? u.advisory))
      .filter(Boolean)
  )].sort();
  return (
    <div className="dashboard-container">
      <div className="top-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div className="title-and-filters">
          <h2 className="section-title">Ticket Summary</h2>
          <div className="filter-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <label><input type="checkbox" checked={showMonthChart} onChange={() => setShowMonthChart(!showMonthChart)} /> Month Chart</label>
            <label><input type="checkbox" checked={showStaffChart} onChange={() => setShowStaffChart(!showStaffChart)} /> Staff Chart</label>
            <label><input type="checkbox" checked={showValueChart} onChange={() => setShowValueChart(!showValueChart)} /> Value Chart</label>
            <label><input type="checkbox" checked={showAdvisory} onChange={() => setShowAdvisory(!showAdvisory)} /> Classes</label>
          </div>
        </div>
  
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Link to={schoolId ? `/${schoolId}/staff-stats` : '/staff-stats'}>
            <button className="blue-button">👩‍🏫 Staff Stats</button>
          </Link>
          <Link to={schoolId ? `/${schoolId}/student-stats` : '/student-stats'}>
            <button className="green-button">📈 Student Stats</button>
          </Link>
  
          <div className="total-box" style={{ background: '#f0f4ff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.75rem 1rem', fontWeight: 'bold', color: '#1e3a8a', minWidth: '220px' }}>
            🎯 Total Tickets This Year: {totalTickets}
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => handleManualAdjustment('schoolTotal', 1)} style={{ backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.75rem' }}>+1</button>
              <button onClick={() => handleManualAdjustment('schoolTotal', -1)} style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.75rem' }}>−1</button>
            </div>
          </div>
        </div>
      </div>
  
      {showMonthChart && (
        <div className="section-card">
          {(() => {
            const totalPerStaff = {};
            Object.values(staffStats).forEach(monthObj => {
              Object.entries(monthObj).forEach(([name, count]) => {
                totalPerStaff[name] = (totalPerStaff[name] || 0) + count;
              });
            });
            return (
              <Bar
                data={{
                  labels: Object.keys(totalPerStaff),
                  datasets: [{ label: 'Tickets by Staff', data: Object.values(totalPerStaff), backgroundColor: colors }]
                }}
                options={{ responsive: true, plugins: { legend: { display: false }, title: { display: true, text: 'Tickets Given by Staff' } } }}
              />
            );
          })()}
        </div>
      )}

{showValueChart && (
  <div className="section-card">
    <Bar
      data={{
        labels: pbisValues.length > 0 ? pbisValues : Object.keys(valueTypeCounts),
        datasets: [{
          label: 'Total Tickets',
          data: (pbisValues.length > 0
            ? pbisValues.map(val => valueTypeCounts[val] || 0)
            : Object.values(valueTypeCounts)
          ),
          backgroundColor: colors
        }]
      }}
      options={{
        responsive: true,
        plugins: {
          legend: { display: false },
          title: { display: true, text: 'Value Type Ticket Totals' }
        }
      }}
    />
  </div>
)}

      {showAdvisory && groups.map((grp) => {
        const students = users.filter(u => u.role === 'student' && (u.class ?? u.advisory) === grp);
        const yearTotal = advisoryYearTotals[grp] ?? 0;
        const balance = students.reduce((sum, s) => sum + (studentBalances[s.id] || 0), 0);
        const displayName = getGroupDisplayName(grp);

        return (
          <div key={grp} className="section-card" style={{ marginTop: '2rem' }}>
            <h4>{displayName} — 🏅 {yearTotal} this year • 🎯 Current Balance: {balance}</h4>
            <div style={{ marginBottom: '0.5rem' }}>
              <button onClick={() => handleManualAdjustment('advisory', 1, grp)} style={{ backgroundColor: '#22c55e', color: 'white', marginRight: '0.25rem', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>+1</button>
              <button onClick={() => handleManualAdjustment('advisory', -1, grp)} style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>−1</button>
            </div>
            {students.length === 0 ? <p>No students.</p> : (
              students
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((s, i) => {
                  const ticketVal = studentBalances[s.id] || 0;
                  return (
                    <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9', padding: '0.5rem', borderRadius: '4px', marginBottom: '0.25rem' }}>
                      <div><strong>{s.name}</strong> — {ticketVal} tickets</div>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => handleManualAdjustment('student', 1, '', s.id)} style={{ backgroundColor: '#4ade80', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>+1</button>
                        <button onClick={() => handleManualAdjustment('student', -1, '', s.id)} style={{ backgroundColor: '#f87171', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>−1</button>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TicketSummary;
